import { Box, Skeleton, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDepartmentFacility } from "../../../../hooks/useDepartmentFacility";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { departmentNameInFacility } from "../../../../utils/facilityDepartments";
import SystemButton from "../../../UIToolkit/SystemButton/SystemButton";
import { useFacilityAnnouncements } from "../hooks";
import { ThreadPreviewList } from "../styled";
import { FacilityAnnouncementItem } from "./FacilityAnnouncementItem";

// Placeholder rows have no identity of their own; fixed keys keep them stable across renders.
const LOADING_ROW_KEYS = ["first", "second", "third", "fourth"] as const;
const LOADING_ROW_HEIGHT = 96;

/** The building's announcements, from any of its departments, newest first — a list, not conversations. */
export const FacilityAnnouncements: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const announcementsQuery = useFacilityAnnouncements(chosenDepartment.department_id);
	const facilityQuery = useDepartmentFacility({ departmentId: chosenDepartment.department_id, enabled: true });

	const departmentNameById = useMemo(() => {
		const building = facilityQuery.data;
		if (!building) return undefined;
		return new Map(
			building.departments.map((department) => [
				department.department_id,
				departmentNameInFacility(department.display_name, building.name, t("header.facilitySwitcher.mainDepartment")),
			]),
		);
	}, [facilityQuery.data, t]);

	if (announcementsQuery.isError || facilityQuery.isError) {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }} data-testid="facility-announcements-error">
				<Typography color="error">{t("announcements.error")}</Typography>
				<SystemButton
					buttonVariant="secondary"
					onClick={() => {
						void announcementsQuery.refetch();
						void facilityQuery.refetch();
					}}
					data-testid="facility-announcements-retry"
				>
					{t("announcements.retry")}
				</SystemButton>
			</Box>
		);
	}

	if (!announcementsQuery.data || !facilityQuery.data || !departmentNameById) {
		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-testid="facility-announcements-loading">
				{LOADING_ROW_KEYS.map((rowKey) => (
					<Skeleton key={rowKey} variant="rounded" width="100%" height={LOADING_ROW_HEIGHT} />
				))}
			</Box>
		);
	}

	const announcements = announcementsQuery.data.pages.flatMap((page) => page.announcements);
	if (announcements.length === 0) {
		return (
			<Typography sx={{ color: "text.secondary" }} data-testid="facility-announcements-empty">
				{t("announcements.empty", { facility: facilityQuery.data.name })}
			</Typography>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
			<ThreadPreviewList data-testid="facility-announcements-list">
				{announcements.map((announcement) => (
					<FacilityAnnouncementItem
						key={announcement.id}
						announcement={announcement}
						facilityName={facilityQuery.data.name}
						departmentNameById={departmentNameById}
						timezone={timezone}
					/>
				))}
			</ThreadPreviewList>
			{announcementsQuery.hasNextPage && (
				<SystemButton
					buttonVariant="borderless"
					loading={announcementsQuery.isFetchingNextPage}
					onClick={() => void announcementsQuery.fetchNextPage()}
					data-testid="facility-announcements-show-older"
				>
					{t("announcements.show.older")}
				</SystemButton>
			)}
		</Box>
	);
};
